import {onMounted, onUnmounted, ref} from 'vue';

/**
 * Composable to handle the display of the fixed table header when scrolling down the page.
 */
export const useFixedHeader = () => {
	const showFixedHeader = ref(false);

	const onScroll = () => {
		const headerTop = document.querySelector('.header-top') as HTMLElement;
		const tableHead = document.querySelector('.js-catalog-overview-table-wrapper') as HTMLElement;

		if (!headerTop || !tableHead) {
			showFixedHeader.value = false;
			return;
		}

		const bottomOfHeaderTop = headerTop.getBoundingClientRect().bottom;
		const topOfTableHead = tableHead.getBoundingClientRect().top;

		showFixedHeader.value = topOfTableHead <= bottomOfHeaderTop;
	};

	onMounted(() => {
		// Wait for the table to be rendered before attaching the scroll event listener.
		setTimeout(() => {
			onScroll();
			window.addEventListener('scroll', onScroll);
		}, 500);
	});

	onUnmounted(() => window.removeEventListener('scroll', onScroll));

	return showFixedHeader;
};

/**
 * Applies the width of the columns of one table to the columns of another table.
 *
 * @param targetTableWrapperClass The class of the wrapper element of the table whose columns should be adjusted.
 * @param sourceTableWrapperClass The class of the wrapper element of the table whose columns should be copied.
 */
const recalculateColumnWidths = (targetTableWrapperClass: string, sourceTableWrapperClass: string) => {
	const sourceTableWrapper = document.querySelector(sourceTableWrapperClass) as HTMLElement;
	const targetTableWrapper = document.querySelector(targetTableWrapperClass) as HTMLElement;

	const sourceTable = sourceTableWrapper?.querySelector('table') as HTMLElement;
	const targetTable = targetTableWrapper?.querySelector('table') as HTMLElement;

	if (!sourceTable || !targetTable) {
		return;
	}

	const sourceColumns = sourceTable.querySelectorAll('thead th') ?? [];

	sourceColumns.forEach((column, index) => {
		const sourceCol = column as HTMLElement;
		const targetCol = targetTable.querySelectorAll('thead th')[index] as HTMLElement;

		targetCol.style.width = `${sourceCol.clientWidth}px`;
		targetCol.style.minWidth = `${sourceCol.clientWidth}px`;
	});
};

const recalculateStaticColumnWidths = () => {
	recalculateColumnWidths(
		'.js-fixed-static-table-wrapper',
		'.js-static-table-wrapper'
	);
};

const recalculateDynamicColumnWidths = () => {
	recalculateColumnWidths(
		'.js-fixed-dynamic-table-wrapper',
		'.js-dynamic-table-wrapper'
	);
};

/**
 * Recalculates the column widths of the fixed table header
 * to align the headers with the actual columns of the table.
 */
export const recalculateTableColumnWidths = () => {
	recalculateStaticColumnWidths();
	recalculateDynamicColumnWidths();
};
